import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TabIconSync } from "@/components/layout/TabIconSync";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { RenamedRoute } from "@/components/RenamedRoute";
import { AuthProvider } from "@/contexts/AuthContext";
import { AdminProvider } from "@/contexts/AdminContext";
import { LocaleProvider } from "@/contexts/LocaleContext";

// Lazy load pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Account = lazy(() => import("./pages/Account"));

const BlendFeedback = lazy(() => import("./pages/BlendFeedback"));
const BlendGradient = lazy(() => import("./pages/BlendGradient"));
const Workshop = lazy(() => import("./pages/Workshop"));
const Whiskey = lazy(() => import("./pages/Whiskey"));
const MaterialPage = lazy(() => import("./pages/MaterialPage"));
const MaterialsIndex = lazy(() => import("./pages/MaterialsIndex"));
const AtlasV4 = lazy(() => import("./pages/AtlasV4"));
const AtlasEntry = lazy(() => import("./pages/AtlasEntry"));
const AtlasMobile = lazy(() => import("./pages/AtlasMobile"));
const AdminSeed = lazy(() => import("./pages/AdminSeed"));
const AdminSetPages = lazy(() => import("./pages/AdminSetPages"));
const AdminLabels = lazy(() => import("./pages/AdminLabels"));
const OnlineLabel = lazy(() => import("./pages/OnlineLabel"));
const SafetyCalculator = lazy(() => import("./pages/SafetyCalculator"));
const RedirectToWebsiteBlends = lazy(() => import("./pages/RedirectToWebsiteBlends"));
const SafetyOverview = lazy(() => import("./pages/SafetyOverview"));
const RollOns = lazy(() => import("./pages/RollOns"));
const NotFound = lazy(() => import("./pages/NotFound"));
// Lazy load toast components to reduce initial bundle
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <AdminProvider>
        <LocaleProvider>
          <TooltipProvider>
            <Suspense fallback={null}>
              <Toaster />
              <Sonner />
            </Suspense>
            <BrowserRouter>
              <TabIconSync />
              <Suspense fallback={<div className="min-h-screen bg-background" />}>
                <Routes>
                  <Route path="/" element={<AtlasEntry />} />
                  <Route path="/atlas" element={<AtlasEntry />} />
                  <Route path="/archive" element={<Index />} />
                  <Route path="/atlas-v4" element={<RenamedRoute to="/atlas" />} />
                  <Route path="/mobile" element={<AtlasMobile />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/account" element={<Account />} />
                  <Route path="/published" element={<RedirectToWebsiteBlends />} />
                  <Route path="/saved" element={<RedirectToWebsiteBlends />} />
                  <Route path="/blends/:slug" element={<AtlasV4 />} />
                  <Route path="/blends/:slug/feedback" element={<BlendFeedback />} />
                  <Route path="/blends/:slug/gradient" element={<BlendGradient />} />
                  <Route path="/workshop/:workshopSlug" element={<Workshop />} />
                  <Route path="/whiskey/:whiskeySlug" element={<Whiskey />} />
                  <Route path="/materials" element={<MaterialsIndex />} />
                  <Route path="/materials/:slug" element={<MaterialPage />} />
                  <Route path="/admin/seed" element={<AdminSeed />} />
                  <Route path="/admin/set-pages" element={<AdminSetPages />} />
                  <Route path="/labels" element={<AdminLabels />} />
                  <Route path="/admin/labels" element={<RenamedRoute to="/labels" />} />
                  <Route path="/label/:bottleId" element={<OnlineLabel />} />
                  <Route path="/calculator" element={<SafetyCalculator />} />
                  <Route path="/overview" element={<SafetyOverview />} />
                  {/* Renamed 2026-10-28 to match the header's path-style nav. */}
                  <Route path="/safety-calculator" element={<RenamedRoute to="/calculator" />} />
                  <Route path="/safety-overview" element={<RenamedRoute to="/overview" />} />
                  <Route path="/roll-ons" element={<RollOns mode="list" />} />
                  <Route path="/roll-ons/new" element={<RollOns mode="new" />} />
                  <Route path="/roll-ons/:number" element={<RollOns mode="view" />} />
                  <Route path="/roll-ons/:number/edit" element={<RollOns mode="edit" />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </LocaleProvider>
      </AdminProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
